import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { ProductType } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { slugFrom } from '../api/product-type-mappers';
import { normalizeSkuCode, skuCodeFrom } from '../api/sku-rule-mappers';
import { useAttributes } from '../hooks/use-attributes';
import { useCustomizations } from '../hooks/use-customizations';
import {
  productTypeFormSchema,
  type ProductTypeFormInput,
  type ProductTypeFormValues,
} from '../schemas/product-type-form-schema';

import { ProductTypeAttributesSection } from './product-type-attributes-section';
import { ProductTypeCustomizationsSection } from './product-type-customizations-section';
import { ProductTypeFaqSection } from './product-type-faq-section';

export const PRODUCT_TYPE_FORM_ID = 'product-type-form';

export interface ProductTypeFormProps {
  /** The saved type; null while creating. */
  productType: ProductType | null;
  defaultValues: ProductTypeFormInput;
  onSubmit: (values: ProductTypeFormValues) => Promise<unknown>;
  isSubmitting: boolean;
  dangerZone?: ReactNode;
}

/**
 * The product type editor: details, attribute assignments, default customizations and suggested
 * FAQ in the main column, sort order and Save in the rail. Registers with the save bar.
 */
export function ProductTypeForm({
  productType,
  defaultValues,
  onSubmit,
  isSubmitting,
  dangerZone,
}: ProductTypeFormProps) {
  const { t } = useTranslation(['productTypes', 'common']);
  const message = useErrorMessage();
  const { can } = usePermissions();
  const readOnly = !can('attributes.write');
  const attributes = useAttributes();
  const customizations = useCustomizations();
  const form = useForm<ProductTypeFormInput, unknown, ProductTypeFormValues>({
    resolver: useI18nResolver(productTypeFormSchema),
    defaultValues,
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  // A saved type keeps its slug and code; a new one follows the name until each is edited by hand.
  const [slugTouched, setSlugTouched] = useState(productType !== null);
  const [skuCodeTouched, setSkuCodeTouched] = useState(productType !== null);
  const { isDirty } = form.formState;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
      form.reset(values);
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  useSaveBar({
    form,
    onSave: submit,
    saving: isSubmitting,
    disabled: readOnly,
    onDiscard: () => {
      form.reset();
      setSlugTouched(productType !== null);
      setSkuCodeTouched(productType !== null);
    },
  });

  const rootError = form.formState.errors.root?.server;

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection title={t('sections.save')} description={t('sections.saveDescription')}>
              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.position')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        form={PRODUCT_TYPE_FORM_ID}
                        disabled={readOnly}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Can permission="attributes.write">
                <Button
                  type="submit"
                  form={PRODUCT_TYPE_FORM_ID}
                  disabled={isSubmitting || !isDirty}
                  className="w-full"
                >
                  {isSubmitting ? t('common:actions.saving') : t('common:actions.save')}
                </Button>
              </Can>
            </FormSection>
            {dangerZone}
          </>
        }
      >
        <form
          id={PRODUCT_TYPE_FORM_ID}
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-6"
        >
          {rootError?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertTitle>{t('saveFailed')}</AlertTitle>
              <AlertDescription>{rootError.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection title={t('sections.details')} description={t('sections.detailsDescription')}>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.name')}</FormLabel>
                    <FormControl>
                      <Input
                        autoFocus={productType === null}
                        disabled={readOnly}
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          if (!slugTouched) {
                            form.clearErrors('slug');
                            form.setValue('slug', slugFrom(e.target.value), { shouldDirty: true });
                          }
                          if (!skuCodeTouched) {
                            form.clearErrors('skuCode');
                            form.setValue('skuCode', skuCodeFrom(e.target.value), {
                              shouldDirty: true,
                            });
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.slug')}</FormLabel>
                    <FormControl>
                      <Input
                        className="font-mono text-[13px]"
                        disabled={readOnly}
                        {...field}
                        onChange={(e) => {
                          setSlugTouched(true);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormDescription>{t('fields.slugHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="skuCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.skuCode')}</FormLabel>
                    <FormControl>
                      <Input
                        className="max-w-40 font-mono text-[13px] uppercase"
                        maxLength={12}
                        autoCapitalize="characters"
                        spellCheck={false}
                        disabled={readOnly}
                        {...field}
                        onChange={(e) => {
                          setSkuCodeTouched(true);
                          field.onChange(normalizeSkuCode(e.target.value));
                        }}
                      />
                    </FormControl>
                    <FormDescription>{t('fields.skuCodeHint')}</FormDescription>
                    {productType ? (
                      <p
                        className="text-muted-foreground text-sm"
                        data-testid="product-type-sku-rule"
                      >
                        {productType.skuRule ? (
                          <>
                            {t(productType.skuRule.isDefault ? 'skuRule.default' : 'skuRule.rule', {
                              name: productType.skuRule.name,
                            })}{' '}
                            <Link
                              to="/org/catalog-setup/sku-rules/$ruleId"
                              params={{ ruleId: productType.skuRule.id }}
                              className="underline underline-offset-4"
                            >
                              {t('skuRule.edit')}
                            </Link>
                          </>
                        ) : (
                          t('skuRule.none')
                        )}
                      </p>
                    ) : null}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('fields.description')}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} disabled={readOnly} {...field} />
                    </FormControl>
                    <FormDescription>{t('fields.descriptionHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>
          <ProductTypeAttributesSection
            attributes={attributes.data ?? []}
            assigned={productType?.attributes ?? []}
            readOnly={readOnly}
          />
          <ProductTypeCustomizationsSection
            customizations={customizations.data ?? []}
            readOnly={readOnly}
          />
          <ProductTypeFaqSection readOnly={readOnly} />
        </form>
      </FormLayout>
    </Form>
  );
}
